import { useWorkspaces } from "@/api/workspaces";
import { useMemo } from "react";

/**
 * The workspaces the actor may write to — the destinations a Move to / Copy
 * to is actually allowed to land in.
 *
 * The server is the authority: it checks `can_edit` on the destination for
 * both verbs and refuses without it, and it reports that capability on the
 * workspace response itself. The picker disables what this set excludes
 * rather than hiding it, so an unwritable workspace reads as "not allowed"
 * instead of quietly missing.
 *
 * Returns `undefined` while the list is in flight; callers treat that as
 * "assume writable" so the picker is never briefly empty.
 */
export function useWritableWorkspaceIds(
  enabled = true,
): ReadonlySet<string> | undefined {
  // `enabled` gates the fetch: this hook mounts on the public share page for
  // anonymous viewers, where an ungated authenticated fetch fails silent
  // token acquisition and retries.
  const { data: workspaces } = useWorkspaces({ enabled });

  return useMemo(() => {
    if (!workspaces) return undefined;
    const writable = new Set<string>();
    for (const ws of workspaces) {
      if (ws.can_edit !== false) writable.add(ws.id);
    }
    return writable;
  }, [workspaces]);
}
